import { elizaLogger } from '@elizaos/core';
import { GraphQLResponse } from './zapperGraphQL';

/**
 * Cache entry structure
 */
export interface CacheEntry<T = any> {
  data: GraphQLResponse<T>;
  timestamp: number;
  ttl: number;
  accessCount: number;
  lastAccessed: number;
  originalQuery: string;
  variables?: Record<string, any>;
}

/**
 * Cache statistics
 */
export interface CacheStats {
  totalRequests: number;
  cacheHits: number;
  cacheMisses: number;
  hitRate: number;
  entryCount: number;
  totalMemoryUsage: number;
  oldestEntry: number;
  newestEntry: number;
}

/**
 * Cache configuration
 */
export interface CacheConfig {
  maxSize: number;
  defaultTTL: number;
  cleanupInterval: number;
  enableStats: boolean;
}

/**
 * Advanced query cache with TTL, LRU eviction, and optimization
 */
export class ZapperQueryCache {
  private cache: Map<string, CacheEntry> = new Map();
  private stats: CacheStats = {
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    hitRate: 0,
    entryCount: 0,
    totalMemoryUsage: 0,
    oldestEntry: 0,
    newestEntry: 0,
  };
  private config: CacheConfig;
  private cleanupTimer: NodeJS.Timeout | null = null;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      maxSize: 1000,
      defaultTTL: 300000, // 5 minutes
      cleanupInterval: 60000, // 1 minute
      enableStats: true,
      ...config,
    };

    // Start cleanup timer
    this.startCleanupTimer();
  }

  /**
   * Generate cache key from query and variables
   */
  private generateCacheKey(query: string, variables?: Record<string, any>): string {
    const variablesStr = variables ? JSON.stringify(variables) : '';
    const combined = query + variablesStr;

    // Simple hash function for cache key
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return `query_${Math.abs(hash)}_${combined.length}`;
  }

  /**
   * Get cached response
   */
  get<T = any>(query: string, variables?: Record<string, any>): GraphQLResponse<T> | null {
    const key = this.generateCacheKey(query, variables);
    const entry = this.cache.get(key);

    if (this.config.enableStats) {
      this.stats.totalRequests++;
    }

    if (!entry) {
      if (this.config.enableStats) {
        this.stats.cacheMisses++;
        this.updateHitRate();
      }
      return null;
    }

    // Check if entry is expired
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      if (this.config.enableStats) {
        this.stats.cacheMisses++;
        this.stats.entryCount--;
        this.updateHitRate();
      }
      return null;
    }

    // Update access statistics
    entry.accessCount++;
    entry.lastAccessed = now;

    if (this.config.enableStats) {
      this.stats.cacheHits++;
      this.updateHitRate();
    }

    return entry.data;
  }

  /**
   * Set cached response
   */
  set<T = any>(
    query: string,
    variables: Record<string, any> | undefined,
    response: GraphQLResponse<T>,
    ttl?: number
  ): void {
    const key = this.generateCacheKey(query, variables);
    const now = Date.now();

    // Check if we need to evict entries
    while (this.cache.size >= this.config.maxSize) {
      this.evictLRU();
    }

    const entry: CacheEntry<T> = {
      data: response,
      timestamp: now,
      ttl: ttl || this.config.defaultTTL,
      accessCount: 1,
      lastAccessed: now,
      originalQuery: query,
      variables: variables,
    };

    this.cache.set(key, entry);

    if (this.config.enableStats) {
      this.stats.entryCount++;
      this.updateMemoryUsage();
      this.updateTimestamps();
    }
  }

  /**
   * Check if query result is cached
   */
  has(query: string, variables?: Record<string, any>): boolean {
    const key = this.generateCacheKey(query, variables);
    const entry = this.cache.get(key);

    if (!entry) return false;

    // Check if entry is expired
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      if (this.config.enableStats) {
        this.stats.entryCount--;
      }
      return false;
    }

    return true;
  }

  /**
   * Invalidate specific cache entry
   */
  invalidate(query: string, variables?: Record<string, any>): boolean {
    const key = this.generateCacheKey(query, variables);
    const deleted = this.cache.delete(key);

    if (deleted && this.config.enableStats) {
      this.stats.entryCount--;
    }

    return deleted;
  }

  /**
   * Invalidate cache entries by pattern
   */
  invalidatePattern(pattern: string): number {
    let deletedCount = 0;

    for (const [key, entry] of this.cache) {
      if (entry.originalQuery.includes(pattern) || key.includes(pattern)) {
        this.cache.delete(key);
        deletedCount++;
      }
    }

    if (this.config.enableStats) {
      this.stats.entryCount -= deletedCount;
    }

    return deletedCount;
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();

    if (this.config.enableStats) {
      this.stats.entryCount = 0;
      this.stats.totalMemoryUsage = 0;
      this.stats.oldestEntry = 0;
      this.stats.newestEntry = 0;
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    if (this.config.enableStats) {
      this.updateMemoryUsage();
      this.updateTimestamps();
    }

    return { ...this.stats };
  }

  /**
   * Update cache configuration
   */
  updateConfig(config: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...config };

    // Restart cleanup timer if interval changed
    if (config.cleanupInterval !== undefined) {
      this.stopCleanupTimer();
      this.startCleanupTimer();
    }
  }

  /**
   * Optimize cache performance
   */
  optimize(): {
    entriesRemoved: number;
    memoryFreed: number;
  } {
    const initialSize = this.cache.size;
    const initialMemory = this.stats.totalMemoryUsage;

    // Remove expired entries
    const now = Date.now();
    for (const [key, entry] of this.cache) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }

    // If still over limit, evict LRU entries
    while (this.cache.size > this.config.maxSize * 0.8) {
      this.evictLRU();
    }

    const finalSize = this.cache.size;
    const finalMemory = this.config.enableStats ? this.calculateMemoryUsage() : 0;

    if (this.config.enableStats) {
      this.stats.entryCount = finalSize;
      this.stats.totalMemoryUsage = finalMemory;
    }

    return {
      entriesRemoved: initialSize - finalSize,
      memoryFreed: initialMemory - finalMemory,
    };
  }

  /**
   * Get cache entries for debugging
   */
  getEntries(): Array<{ key: string; entry: CacheEntry }> {
    return Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      entry: { ...entry },
    }));
  }

  /**
   * Destroy cache and cleanup resources
   */
  destroy(): void {
    this.stopCleanupTimer();
    this.clear();
  }

  /**
   * Start cleanup timer
   */
  private startCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanupExpiredEntries();
    }, this.config.cleanupInterval);
  }

  /**
   * Stop cleanup timer
   */
  private stopCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Clean up expired entries
   */
  private cleanupExpiredEntries(): void {
    const now = Date.now();
    let removedCount = 0;

    for (const [key, entry] of this.cache) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      elizaLogger.debug(`Cache cleanup: removed ${removedCount} expired entries`);

      if (this.config.enableStats) {
        this.stats.entryCount -= removedCount;
      }
    }
  }

  /**
   * Evict least recently used entry
   */
  private evictLRU(): void {
    let oldestKey: string | null = null;
    let oldestTime = Date.now();

    for (const [key, entry] of this.cache) {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      if (this.config.enableStats) {
        this.stats.entryCount--;
      }
    }
  }

  /**
   * Update hit rate statistic
   */
  private updateHitRate(): void {
    if (this.stats.totalRequests > 0) {
      this.stats.hitRate = (this.stats.cacheHits / this.stats.totalRequests) * 100;
    }
  }

  /**
   * Update memory usage estimate
   */
  private updateMemoryUsage(): void {
    this.stats.totalMemoryUsage = this.calculateMemoryUsage();
  }

  /**
   * Calculate memory usage estimate
   */
  private calculateMemoryUsage(): number {
    let totalSize = 0;

    for (const [key, entry] of this.cache) {
      // Rough estimate: key size + data size
      totalSize += key.length * 2; // Unicode characters
      totalSize += JSON.stringify(entry.data).length * 2;
      totalSize += 64; // Overhead for entry metadata
    }

    return totalSize;
  }

  /**
   * Update timestamp statistics
   */
  private updateTimestamps(): void {
    if (this.cache.size === 0) {
      this.stats.oldestEntry = 0;
      this.stats.newestEntry = 0;
      return;
    }

    let oldest = Date.now();
    let newest = 0;

    for (const entry of this.cache.values()) {
      if (entry.timestamp < oldest) {
        oldest = entry.timestamp;
      }
      if (entry.timestamp > newest) {
        newest = entry.timestamp;
      }
    }

    this.stats.oldestEntry = oldest;
    this.stats.newestEntry = newest;
  }
}

/**
 * Query optimization utilities
 */
export class QueryOptimizer {
  /**
   * Optimize GraphQL query by removing unnecessary fields
   */
  static removeUnusedFields(query: string, usedFields: string[]): string {
    // Simple implementation - in production, would use GraphQL AST
    let optimized = query;

    // Remove comments
    optimized = optimized.replace(/\s*#.*$/gm, '');

    // Remove extra whitespace
    optimized = optimized.replace(/\s+/g, ' ').trim();

    return optimized;
  }

  /**
   * Merge multiple queries into a single batch query
   */
  static mergeQueries(queries: Array<{ alias: string; query: string }>): string {
    const mergedFields = queries
      .map(({ alias, query }) => {
        // Extract the query body (simplified)
        const match = query.match(/{\s*([^}]+)\s*}/);
        const body = match ? match[1] : '';
        return `${alias}: ${body}`;
      })
      .join('\n  ');

    return `query BatchQuery {\n  ${mergedFields}\n}`;
  }

  /**
   * Optimize query variables
   */
  static optimizeVariables(variables: Record<string, any>): Record<string, any> {
    const optimized: Record<string, any> = {};

    for (const [key, value] of Object.entries(variables)) {
      // Skip null/undefined values
      if (value != null) {
        optimized[key] = value;
      }
    }

    return optimized;
  }

  /**
   * Calculate query complexity score
   */
  static calculateComplexity(query: string): number {
    let complexity = 0;

    // Count nested levels
    const maxDepth = (query.match(/{/g) || []).length;
    complexity += maxDepth * 2;

    // Count fields
    const fieldCount = (query.match(/\w+(?=\s*[:{])/g) || []).length;
    complexity += fieldCount;

    // Count variables
    const variableCount = (query.match(/\$\w+/g) || []).length;
    complexity += variableCount;

    return complexity;
  }

  /**
   * Suggest query optimizations
   */
  static suggestOptimizations(query: string): string[] {
    const suggestions: string[] = [];

    // Check for common optimization opportunities
    if (query.includes('...')) {
      suggestions.push('Consider using fragments to reduce query duplication');
    }

    if (query.length > 2000) {
      suggestions.push('Query is quite large - consider splitting into smaller queries');
    }

    if (this.calculateComplexity(query) > 50) {
      suggestions.push('Query complexity is high - consider reducing nesting levels');
    }

    if (query.includes('*')) {
      suggestions.push('Avoid wildcard selections - specify only needed fields');
    }

    return suggestions;
  }
}

/**
 * Cache preloader for common queries
 */
export class CachePreloader {
  private cache: ZapperQueryCache;
  private preloadSchedule: Map<string, NodeJS.Timeout> = new Map();

  constructor(cache: ZapperQueryCache) {
    this.cache = cache;
  }

  /**
   * Schedule preloading of common queries
   */
  schedulePreload(
    query: string,
    variables: Record<string, any>,
    fetcher: () => Promise<GraphQLResponse>,
    interval: number = 300000 // 5 minutes
  ): void {
    const key = this.cache['generateCacheKey'](query, variables);

    // Clear existing schedule
    const existingTimer = this.preloadSchedule.get(key);
    if (existingTimer) {
      clearInterval(existingTimer);
    }

    // Schedule preloading
    const timer = setInterval(async () => {
      try {
        const response = await fetcher();
        this.cache.set(query, variables, response);
        elizaLogger.debug(`Preloaded cache for query: ${key}`);
      } catch (error) {
        elizaLogger.error(`Failed to preload cache for query: ${key}`, error);
      }
    }, interval);

    this.preloadSchedule.set(key, timer);
  }

  /**
   * Cancel preloading for a query
   */
  cancelPreload(query: string, variables?: Record<string, any>): void {
    const key = this.cache['generateCacheKey'](query, variables);
    const timer = this.preloadSchedule.get(key);

    if (timer) {
      clearInterval(timer);
      this.preloadSchedule.delete(key);
    }
  }

  /**
   * Cancel all preloading
   */
  cancelAllPreloads(): void {
    for (const timer of this.preloadSchedule.values()) {
      clearInterval(timer);
    }
    this.preloadSchedule.clear();
  }

  /**
   * Get preload schedule info
   */
  getScheduleInfo(): Array<{ key: string; active: boolean }> {
    return Array.from(this.preloadSchedule.keys()).map((key) => ({
      key,
      active: true,
    }));
  }
}

/**
 * Default export for convenience
 */
export default {
  ZapperQueryCache,
  QueryOptimizer,
  CachePreloader,
};
